import React, { useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import MilestoneForm from "@pages/Program/components/milestones/MilestoneForm";
import { MilestonePayload } from "@shared/interfaces/ProgramFiles";
import useHandleSaveMilestone from "@pages/Program/components/milestones/utils/useHandleSaveMilestone";
import { useParams } from "react-router-dom";
import Loader from "@shared/components/Loader";

interface AddMilestoneProps {
  isOpen: boolean;
  onClose: () => void;
  fetchMilestone: () => void;
  startDate: string;
  endDate: string;
}

const AddMilestone: React.FC<AddMilestoneProps> = ({
  isOpen,
  onClose,
  fetchMilestone,
  startDate,
  endDate,
}) => {
  const methods = useForm<MilestonePayload>({
    mode: "all",
    reValidateMode: "onChange",
    criteriaMode: "all",
    defaultValues: {
      name: "",
      description: "",
      start_date: "",
      end_date: "",
      project_id: undefined,
      template_id: undefined,
      responsible_user_id: undefined,
      goal_id: undefined, // Added goal_id to default values
    },
  });

  const {
    handleSubmit,
    formState: { isValid },
    reset,
  } = methods;

  const handleSaveMilestone = useHandleSaveMilestone({}, methods);
  const [loading, setLoading] = useState(false);

  const { programId } = useParams();

  const onSubmit = async (values: MilestonePayload) => {
    if (!isValid || !programId) return;

    try {
      setLoading(true);
      const payload: MilestonePayload = {
        ...values,
        reporting_frequency: null,
        program_id: Number(programId),
      };

      await handleSaveMilestone(payload);
      reset({
        name: "",
        description: "",
        start_date: "",
        end_date: "",
        project_id: undefined,
        template_id: undefined,
        responsible_user_id: undefined,
      });
      fetchMilestone();
      onClose();
    } catch (error) {
      console.error("Error saving milestone:", error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-30 flex items-center justify-center">
      <FormProvider {...methods}>
        <div className="bg-white rounded-lg w-full max-w-2xl py-6 shadow-lg h-[95vh] flex flex-col">
          <h2 className="text-xl font-semibold pb-4 mb-0 border-b border-[#e5e7eb] px-6">
            Add a task to Project
          </h2>

          <MilestoneForm startDate={startDate} endDate={endDate} />
          <div className="flex justify-end mt-6 gap-3 px-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded-md text-gray-700 hover:bg-gray-100"
            >
              Close
            </button>
            <button
              type="submit"
              disabled={!isValid || loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleSubmit(onSubmit)}
            >
              {loading ? <Loader color="#fff" /> : <span>Save Task</span>}
            </button>
          </div>
        </div>
      </FormProvider>
    </div>
  );
};

export default AddMilestone;
